import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Card, CardContent } from "../../ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../ui/dialog";
import {
    Shield,
    Clock,
    FileText,
    CheckCircle2
} from "lucide-react";
import { Device } from "../interfaces/EndpointInterfaces";
import useUserStore from "../../../services/userStore";
import { myLocalStorage } from "../../StorageHelper";
import { submitForm } from "./Api/tenantuserendpointsapi";

interface TenantResourceRequestProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    resourceName: string;
    resourceType?: string;
    deviceName?: string;
    userId?: string;
    resourceId?: string;
    device: Device;
    user: any;
    publisher: string;
}

export default function TenantResourceRequest({
    open,
    onOpenChange,
    resourceName,
    resourceType = "Resource",
    deviceName = "",
    userId,
    user,
    resourceId,
    device,
    publisher
}: TenantResourceRequestProps) {
    const [accessType, setAccessType] = useState("DYNAMIC");
    const [justification, setJustification] = useState("");
    const [timeMinutes, setTimeMinutes] = useState("");
    let { email, role, userRole } = useUserStore((state) => state.user);
    let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;

    // Extract filename from path for cleaner display
    const getDisplayName = (path: string) => {
        if (!path) return "Resource";

        // For file paths, extract just the filename
        if (path.includes("\\") || path.includes("/")) {
            const parts = path.split(/[\\\/]/);
            return parts[parts.length - 1] || path;
        }

        return path;
    };

    const displayName = getDisplayName(resourceName);


    const isDeviceSSh = (device: Device) => {
        return (
            device?.OperatingSystem?.toLowerCase().includes("linux") ||
            device?.OperatingSystem?.toLowerCase().includes("macos")
        );
    };

    const handleSubmit = async () => {


        const isServer = device?.belongedToUser
            ? device.belongedToUser === email
                ? false
                : device?.isServer
            : device?.isServer;

        const payload = {
            userId: userId,
            username: user.cn,
            email:
                isDeviceSSh(device)
                    ? email
                    : user?.email,
            resourceType: resourceType,
            resourceId: resourceId,
            resourceName,
            computerName:
                isDeviceSSh(device)
                    ? user?.email
                    : device?.dNSHostName,
            friendlyName: device?.friendlyName,
            resourcePath: resourceName || "",
            requestMessage: justification,
            requestStatus: "PENDING",
            selectedTime: accessType.toUpperCase() === "PERMANENT" ? 0 : timeMinutes,
            publisherId: resourceType == "APP" ? publisher : null,
            computerId: device?.id,
            createdAt: new Date().toISOString(),
            privilegeLevel: accessType.toUpperCase(),
            tenantName,
            userEmail: email,
            toConnect: false,
            isServer: isServer,
            machineUser: user.cn,
            machineUserId: userId,
            connectionMode: null,
            connectionUser: null,
            connectionLabel: null,
            type: "onPrem",
            timingMode: accessType.toUpperCase() === "PERMANENT" ? "PERMANENT" : "GENERIC",
            scheduledStart: null,
            scheduledEnd: null,
        };



        await submitForm(payload);
        handleCancel();
    };

    const handleCancel = () => {
        onOpenChange(false);
        // Reset form
        setAccessType("DYNAMIC");
        setJustification("");
        setTimeMinutes("");
    };

    // Reset form when dialog closes
    useEffect(() => {
        if (!open) {
            setAccessType("timed");
            setJustification("");
            setTimeMinutes("");
        }
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader className="space-y-2">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/20 dark:to-blue-800/30 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <DialogTitle className="text-xl font-semibold">
                                Request Access
                            </DialogTitle>
                            <p className="text-sm text-muted-foreground">
                                Request access to <span className="font-medium text-foreground">{displayName}</span>
                            </p>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                    {/* Access Type */}
                    <div>
                        <Label className="text-sm font-medium mb-3 block flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Access Type
                        </Label>
                        <RadioGroup value={accessType} onValueChange={setAccessType} className="grid grid-cols-2 gap-3">
                            <div className="relative">
                                <RadioGroupItem value="DYNAMIC" id="DYNAMIC" className="peer sr-only" />
                                <Label
                                    htmlFor="DYNAMIC"
                                    className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover-elevate cursor-pointer peer-data-[state=checked]:border-blue-600 peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-blue-950/20 transition-all"
                                >
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                                        <span className="font-medium text-sm">Timed</span>
                                    </div>
                                </Label>
                            </div>
                            <div className="relative">
                                <RadioGroupItem value="PERMANENT" id="PERMANENT" className="peer sr-only" />
                                <Label
                                    htmlFor="PERMANENT"
                                    className="flex items-center justify-center rounded-lg border-2 border-muted bg-popover p-3 hover-elevate cursor-pointer peer-data-[state=checked]:border-blue-600 peer-data-[state=checked]:bg-blue-50 dark:peer-data-[state=checked]:bg-blue-950/20 transition-all"
                                >
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                                        <span className="font-medium text-sm">Permanent</span>
                                    </div>
                                </Label>
                            </div>
                        </RadioGroup>
                    </div>

                    {/* Request Details */}
                    <Card>
                        <CardContent className="pt-3 space-y-3">
                            <div className="space-y-3">
                                <div>
                                    <Label className="text-xs text-muted-foreground">{resourceType}</Label>
                                    <div className="font-medium text-sm break-all font-mono leading-relaxed">
                                        {resourceName}
                                    </div>
                                </div>
                                {deviceName && (
                                    <div className="pt-2 border-t">
                                        <div className="text-xs text-muted-foreground">
                                            Computer: <span className="font-medium">{deviceName}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Justification and Configuration */}
                    <Card>
                        <CardContent className="pt-3 space-y-3">
                            {/* Justification */}
                            <div>
                                <Label htmlFor="justification" className="text-sm font-medium mb-2 block flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    Access Justification *
                                </Label>
                                <Textarea
                                    id="justification"
                                    value={justification}
                                    onChange={(e) => setJustification(e.target.value)}
                                    placeholder="Provide business justification for this access request..."
                                    className="min-h-[80px] resize-none"
                                    data-testid="textarea-justification"
                                />
                            </div>

                            {/* Time Input */}
                            {accessType === "DYNAMIC" && (
                                <div>
                                    <Label htmlFor="time-minutes" className="text-sm font-medium mb-2 block flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        Duration (minutes) *
                                    </Label>
                                    <Input
                                        id="time-minutes"
                                        value={timeMinutes}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === "") {
                                                setTimeMinutes("");
                                                return;
                                            }
                                            if (!/^\d+$/.test(value)) return;
                                            const num = Number(value);
                                            if (num >= 1 && num <= 999) {
                                                setTimeMinutes(value);
                                            }
                                        }}
                                        onKeyDown={(e) => {
                                            if (["e", "E", "+", "-", "."].includes(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        placeholder="e.g., 60"
                                        type="number"
                                        min="1"
                                        max="999"
                                        className="max-w-xs"
                                        data-testid="input-time-minutes"
                                    />
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-3 border-t">
                        <Button variant="outline" onClick={handleCancel} data-testid="button-cancel" size="sm">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={
                                !justification.trim() ||
                                (accessType === "DYNAMIC" && !timeMinutes)
                            }
                            data-testid="button-submit"
                            size="sm"
                            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                        >
                            <Shield className="w-4 h-4 mr-1" />
                            Submit Request
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}